import React from 'react';
import { MODE_NAMES, arpeggioTypesFor } from '../../lib/theory';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  Tray, ControlCard, TrayLabel, KeyRows, StringsCard, StandardQuickButtons,
} from '../shared';

// ── Data ──────────────────────────────────────────────────────────────────────
// Only display labels live here. The option VALUES are the state values App.js and the
// SEO deep links use ('Full Scale', 'Pentatonic', 'Modes', 'Arpeggios', ...).

/** Structure labels: board 18 (phone) uses the short ones, board 20 (desktop) spells them out. */
const STRUCTURE_OPTIONS_PHONE = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Penta' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Arpeggios', label: 'Arps' },
];
const STRUCTURE_OPTIONS_DESKTOP = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Pentatonic' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Arpeggios', label: 'Arpeggios' },
];

/** Strings low to high, matching guitarStringFocus. */
const STRINGS = [
  { label: 'E', ariaLabel: 'Low E string' },
  { label: 'A', ariaLabel: 'A string' },
  { label: 'D', ariaLabel: 'D string' },
  { label: 'G', ariaLabel: 'G string' },
  { label: 'B', ariaLabel: 'B string' },
  { label: 'e', ariaLabel: 'High E string' },
];

/**
 * CAGED Shape tray, shown for the scale views (Scale, Penta, Modes). Values are the Fretboard's
 * shape names; 'All Shapes' draws the whole neck. Listed in CAGED order, which is also the order
 * the shapes climb the neck.
 */
const SHAPE_OPTIONS = [
  { value: 'All Shapes', label: 'All', ariaLabel: 'All shapes' },
  { value: 'C Shape', label: 'C', ariaLabel: 'C shape' },
  { value: 'A Shape', label: 'A', ariaLabel: 'A shape' },
  { value: 'G Shape', label: 'G', ariaLabel: 'G shape' },
  { value: 'E Shape', label: 'E', ariaLabel: 'E shape' },
  { value: 'D Shape', label: 'D', ariaLabel: 'D shape' },
];

// The 3 NPS system stays hidden (rebuild plan, open question 1); App.js pins guitarSystem to
// 'CAGED'. The 3 NPS code in theory.js and the Fretboard is kept.

/** 'b3' -> 'flat 3', for screen readers. */
const spokenDegree = (label) => label.replace(/^b/, 'flat ');

/**
 * Private lesson patterns (src/lib/lesson-patterns.js, data behind the Sign in). App.js passes
 * `lessonTrays` only while signed in and on the Scale view in Major. Signed out it is null and
 * nothing below renders. Each tray's name comes from the private data, not from this file.
 */
function lessonOptions(patterns) {
  return patterns.map((p) => ({
    value: p.id,
    label: p.label,
    ariaLabel: `Pattern ${spokenDegree(p.label)}`,
  }));
}

// ── Controls: Key card + Strings card ─────────────────────────────────────────

/**
 * Guitar controls (board 18 phone / board 20 desktop): the Key and Strings cards.
 * The quick buttons live in {@link GuitarQuickButtons}; the page layout puts them under the cards.
 * `guitarSystem` is accepted but unused while 3 NPS is hidden.
 */
export default function ControlPanel({
  guitarKey,        onGuitarKeyChange,
  guitarTonality,   onGuitarTonalityChange,
  guitarStructure,  onGuitarStructureChange,
  guitarMode,       onGuitarModeChange,
  guitarStringFocus, onGuitarStringToggle,
  guitarArpeggioType, onGuitarArpeggioTypeChange,
  guitarShape,       onGuitarShapeChange,
  lessonTrays = null,      // [{ group, patterns }] for this view, or null (signed out / none here)
  guitarLessonPattern = null, onGuitarLessonPatternChange,
}) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 'md' : 'sm';
  const isModes = guitarStructure === 'Modes';
  const isArpeggios = guitarStructure === 'Arpeggios';
  const { basic, extensions } = arpeggioTypesFor(guitarTonality);

  return (
    <>
      <ControlCard title="Key" size={size}>
        <KeyRows
          isDesktop={isDesktop}
          keyValue={guitarKey}
          onKeyChange={onGuitarKeyChange}
          tonality={guitarTonality}
          onTonalityChange={onGuitarTonalityChange}
          // A mode sets its own quality, so Major/Minor sits out while Modes is on.
          tonalityDisabled={isModes}
          structureOptions={STRUCTURE_OPTIONS_PHONE}
          structureOptionsDesktop={STRUCTURE_OPTIONS_DESKTOP}
          structure={guitarStructure}
          onStructureChange={onGuitarStructureChange}
        />

        {isModes && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Mode</TrayLabel>}
            <Tray
              ariaLabel="Mode"
              options={MODE_NAMES}
              value={guitarMode}
              onChange={onGuitarModeChange}
              columns={isDesktop ? 3 : 4}
            />
          </>
        )}

        {!isArpeggios && (
          <>
            {/* Labelled on the phone too: bare C A G E D letters would read as more key buttons. */}
            <TrayLabel className="mt-1">CAGED shape</TrayLabel>
            <Tray
              ariaLabel="CAGED shape"
              options={SHAPE_OPTIONS}
              // While a lesson pattern is on it replaces the CAGED window, so no shape is raised
              value={guitarLessonPattern ? null : guitarShape}
              onChange={onGuitarShapeChange}
              columns={SHAPE_OPTIONS.length}
              size={isDesktop ? 'md' : 'sm'}
            />
          </>
        )}

        {lessonTrays && lessonTrays.map(({ group, label, patterns }) => (
          <React.Fragment key={group}>
            {/* Labelled on the phone too, like the CAGED tray: bare numbers would read as keys. */}
            <TrayLabel className="mt-1">{label}</TrayLabel>
            <Tray
              ariaLabel={label}
              options={lessonOptions(patterns)}
              // One pattern at a time across both trays; the other tray shows none raised
              value={guitarLessonPattern}
              onChange={onGuitarLessonPatternChange}
              columns={patterns.length}
              size={isDesktop ? 'md' : 'sm'}
            />
          </React.Fragment>
        ))}

        {isArpeggios && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Basic</TrayLabel>}
            <Tray
              ariaLabel="Basic arpeggio type"
              options={basic}
              value={guitarArpeggioType}
              onChange={onGuitarArpeggioTypeChange}
              columns={5}
            />
            {isDesktop && <TrayLabel>Extended</TrayLabel>}
            <Tray
              ariaLabel="Extended arpeggio type"
              options={extensions}
              value={guitarArpeggioType}
              onChange={onGuitarArpeggioTypeChange}
              {...(isDesktop ? { columns: 5 } : { scroll: true })}
            />
          </>
        )}
      </ControlCard>

      <StringsCard strings={STRINGS} focus={guitarStringFocus} onToggle={onGuitarStringToggle} size={size} />
    </>
  );
}

// ── Quick buttons: Filter / Labels / Reset ────────────────────────────────────

/**
 * The three quick buttons for the layout's `quickButtons` slot.
 * Filter cycles All Notes -> Triads Only -> Roots Only and is greyed for Arpeggios (every chord tone
 * always shows). Labels toggles Note Names <-> Intervals ('None' counts as Names).
 */
export function GuitarQuickButtons({
  guitarStructure,
  guitarFilter,  onGuitarFilterChange,
  guitarLabel,   onGuitarLabelChange,
  onGuitarReset,
}) {
  const isDesktop = useIsDesktop();
  return (
    <StandardQuickButtons
      size={isDesktop ? 'md' : 'sm'}
      filter={guitarFilter}
      onFilterChange={onGuitarFilterChange}
      filterDisabled={guitarStructure === 'Arpeggios'}
      label={guitarLabel}
      onLabelChange={onGuitarLabelChange}
      onReset={onGuitarReset}
    />
  );
}
